import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AdminSidebar from '../components/AdminSidebar';
import {
  Upload,
  Palette,
  Image,
  Type,
  Save,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const { token, branding, refreshBranding } = useAuth();
  const [formData, setFormData] = useState({
    site_title: '',
    primary_color: '#DC2626',
    background_color: '#09090B'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBg, setIsUploadingBg] = useState(false);

  useEffect(() => {
    if (branding) {
      setFormData({
        site_title: branding.site_title || 'NOW Upload Portal',
        primary_color: branding.primary_color || '#DC2626',
        background_color: branding.background_color || '#09090B'
      });
      setLogoPreview(branding.logo_url);
      setBgPreview(branding.background_image_url);
    }
  }, [branding]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleBgChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBgFile(file);
      setBgPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    setIsUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);

      await axios.post(`${API}/admin/branding/logo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully');
      setLogoFile(null);
      refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleUploadBg = async () => {
    if (!bgFile) return;

    setIsUploadingBg(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', bgFile);

      await axios.post(`${API}/admin/branding/background`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Background image uploaded successfully');
      setBgFile(null);
      refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload background image');
    } finally {
      setIsUploadingBg(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Only send title and colors — file URLs are managed by upload endpoints
      const payload = {
        site_title: formData.site_title,
        primary_color: formData.primary_color,
        background_color: formData.background_color
      };
      
      console.log('Saving branding with payload:', payload);
      
      const response = await axios.put(`${API}/admin/branding`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Save response:', response.data);
      toast.success('Settings saved successfully');
      refreshBranding();
    } catch (error) {
      console.error('Save error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const colorPresets = [
    { name: 'Broadcast Red', value: '#DC2626' },
    { name: 'Signal Blue', value: '#3B82F6' },
    { name: 'Studio Green', value: '#22C55E' },
    { name: 'Amber', value: '#F59E0B' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' }
  ];

  const bgColorPresets = [
    { name: 'Dark Black', value: '#09090B' },
    { name: 'Navy Blue', value: '#0A1628' },
    { name: 'Dark Gray', value: '#1A1A1A' },
    { name: 'Charcoal', value: '#2D2D2D' },
    { name: 'Deep Purple', value: '#1A0A28' },
    { name: 'Dark Teal', value: '#0A1A1A' }
  ];

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-settings">
      <AdminSidebar />
      
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display text-4xl text-white tracking-tight">BRANDING SETTINGS</h1>
            <p className="text-zinc-500 mt-1">Customize the look and feel of your upload portal</p>
          </div>

          {/* Logo Section */}
          <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl mb-6">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white tracking-tight flex items-center gap-2">
                <Image className="w-5 h-5 text-red-500" />
                LOGO
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Upload your company logo (PNG, JPG, SVG, or WebP)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {/* Logo Preview */}
                <div className="w-32 h-32 bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img 
                      src={logoPreview.startsWith('blob:') ? logoPreview : `${process.env.REACT_APP_BACKEND_URL}${logoPreview}`} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-zinc-600">
                      <Upload className="w-8 h-8 mb-2" />
                      <span className="text-xs">No logo</span>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-zinc-400 uppercase text-xs">Select Logo File</Label>
                    <Input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.webp"
                      onChange={handleLogoChange}
                      className="mt-2 bg-zinc-800 border-zinc-700 text-white file:bg-zinc-700 file:text-white file:border-0 file:mr-4 file:px-4 file:py-2 file:rounded-lg cursor-pointer"
                      data-testid="logo-file-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleUploadLogo}
                    disabled={isUploadingLogo || !logoFile}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="upload-logo-btn"
                  >
                    {isUploadingLogo ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </>
                    )}
                  </Button>
                  {!logoFile && (
                    <p className="text-zinc-500 text-xs">Select a file first, then click Upload Logo</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Site Title Section */}
          <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl mb-6">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white tracking-tight flex items-center gap-2">
                <Type className="w-5 h-5 text-red-500" />
                SITE TITLE
              </CardTitle>
              <CardDescription className="text-zinc-500">
                The name displayed in the header and browser tab
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Title</Label>
                <Input
                  value={formData.site_title}
                  onChange={(e) => setFormData({ ...formData, site_title: e.target.value })}
                  placeholder="NOW Upload Portal"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="site-title-input"
                />
              </div>
              
              {/* Preview */}
              <div className="mt-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                <p className="text-zinc-500 text-xs uppercase mb-2">Preview</p>
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <img 
                      src={logoPreview.startsWith('blob:') ? logoPreview : `${process.env.REACT_APP_BACKEND_URL}${logoPreview}`} 
                      alt="Logo" 
                      className="h-9 w-auto object-contain"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: formData.primary_color }}>
                      <Upload className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="font-display text-xl text-white tracking-tight">
                    {formData.site_title || 'NOW Upload Portal'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Background Image Section */}
          <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl mb-6">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white tracking-tight flex items-center gap-2">
                <Image className="w-5 h-5 text-red-500" />
                LOGIN BACKGROUND IMAGE
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Upload a background image for the login page (PNG, JPG, or WebP)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {/* Background Preview */}
                <div className="w-48 h-32 bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden rounded-lg">
                  {bgPreview ? (
                    <img 
                      src={bgPreview.startsWith('blob:') ? bgPreview : `${process.env.REACT_APP_BACKEND_URL}${bgPreview}`} 
                      alt="Background preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-zinc-600">
                      <Image className="w-8 h-8 mb-2" />
                      <span className="text-xs">No background</span>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-zinc-400 uppercase text-xs">Select Background Image</Label>
                    <Input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={handleBgChange}
                      className="mt-2 bg-zinc-800 border-zinc-700 text-white file:bg-zinc-700 file:text-white file:border-0 file:mr-4 file:px-4 file:py-2 file:rounded-lg cursor-pointer"
                      data-testid="bg-file-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleUploadBg}
                    disabled={isUploadingBg || !bgFile}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="upload-bg-btn"
                  >
                    {isUploadingBg ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Background
                      </>
                    )}
                  </Button>
                  {!bgFile && (
                    <p className="text-zinc-500 text-xs">Select a file first, then click Upload Background</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Color Theme Section */}
          <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl mb-6">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white tracking-tight flex items-center gap-2">
                <Palette className="w-5 h-5 text-red-500" />
                PRIMARY COLOR
              </CardTitle>
              <CardDescription className="text-zinc-500">
                The main accent color used throughout the portal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 border border-zinc-700"
                    style={{ backgroundColor: formData.primary_color }}
                  />
                  <Input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    placeholder="#DC2626"
                    className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono"
                    data-testid="primary-color-input"
                  />
                  <Input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-zinc-800 border-zinc-700 cursor-pointer"
                  />
                </div>
                
                {/* Color Presets */}
                <div>
                  <p className="text-zinc-500 text-xs uppercase mb-2">Presets</p>
                  <div className="flex flex-wrap gap-2">
                    {colorPresets.map(preset => (
                      <button
                        key={preset.value}
                        onClick={() => setFormData({ ...formData, primary_color: preset.value })}
                        className={`flex items-center gap-2 px-3 py-2 border transition-colors ${
                          formData.primary_color === preset.value 
                            ? 'border-white bg-zinc-800' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <div 
                          className="w-4 h-4"
                          style={{ backgroundColor: preset.value }}
                        />
                        <span className="text-xs text-zinc-300">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Background Color Section */}
          <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl mb-6">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white tracking-tight flex items-center gap-2">
                <Palette className="w-5 h-5 text-red-500" />
                BACKGROUND COLOR
              </CardTitle>
              <CardDescription className="text-zinc-500">
                The background color for the entire website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 border border-zinc-700"
                    style={{ backgroundColor: formData.background_color }}
                  />
                  <Input
                    type="text"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    placeholder="#09090B"
                    className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono"
                    data-testid="background-color-input"
                  />
                  <Input
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-zinc-800 border-zinc-700 cursor-pointer"
                  />
                </div>
                
                {/* Background Color Presets */}
                <div>
                  <p className="text-zinc-500 text-xs uppercase mb-2">Presets</p>
                  <div className="flex flex-wrap gap-2">
                    {bgColorPresets.map(preset => (
                      <button
                        key={preset.value}
                        onClick={() => setFormData({ ...formData, background_color: preset.value })}
                        className={`flex items-center gap-2 px-3 py-2 border transition-colors ${
                          formData.background_color === preset.value 
                            ? 'border-white bg-zinc-800' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <div 
                          className="w-4 h-4 border border-zinc-600"
                          style={{ backgroundColor: preset.value }}
                        />
                        <span className="text-xs text-zinc-300">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="bg-red-600 hover:bg-red-700 text-white btn-glow"
              data-testid="save-settings-btn"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
